import { useAuth } from '@clerk/clerk-react';
import useSWR, { type ScopedMutator } from 'swr';
import { investmentsApi } from '@/lib/api';
import type { InvestmentItem } from '@/types';
import { swrKeyStartsWith, useAuthedFetcher } from './swrConfig';

const HOLDINGS_KEY = 'investment-holdings';

// The largest page the API serves; a portfolio rarely needs a second one.
const HOLDINGS_PAGE_SIZE = 200;

/**
 * Every live holding in the portfolio — not one list page — for the maturity calendar. Keyed by
 * user, so another account signing in never sees these, and idle while signed out.
 */
export function useInvestmentHoldings() {
  const { userId } = useAuth();
  const authedFetch = useAuthedFetcher();

  return useSWR<InvestmentItem[]>(userId ? [HOLDINGS_KEY, userId] : null, () =>
    authedFetch(async (token) => {
      const holdings: InvestmentItem[] = [];
      let offset: number | null | undefined = 0;
      while (offset != null) {
        const page = await investmentsApi.getAll(token, { limit: HOLDINGS_PAGE_SIZE, offset });
        holdings.push(...page.positions.items);
        offset = page.positions.nextOffset;
      }
      return holdings;
    }),
  );
}

/** Re-reads the holdings after a grouping decision changed the portfolio, so the calendar follows it. */
export function revalidateInvestmentHoldings(mutateGlobal: ScopedMutator): void {
  void mutateGlobal((key) => swrKeyStartsWith(key, [HOLDINGS_KEY]));
}
